import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { personService } from "../services/personService";
import type { PersonInsert, PersonUpdate, PersonAddressInsert } from "../types";
import { toast } from "sonner";

interface PersonListParams {
  pagina: number;
  tamanhoPagina: number;
}

export const usePersons = (params: PersonListParams = { pagina: 1, tamanhoPagina: 20 }) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["persons", params.pagina, params.tamanhoPagina],
    queryFn: () => personService.getAll(params),
  });

  const createMutation = useMutation({
    mutationFn: ({ person }: { person: PersonInsert; addresses?: PersonAddressInsert[] }) =>
      personService.create(person),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["persons"] });
      toast.success("Pessoa cadastrada com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao cadastrar pessoa: ${error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: PersonUpdate }) =>
      personService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["persons"] });
      toast.success("Pessoa atualizada com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar pessoa: ${error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => personService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["persons"] });
      toast.success("Pessoa excluída com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao excluir pessoa: ${error.message}`);
    },
  });

  return {
    persons:      query.data?.itens ?? [],
    total:        query.data?.total ?? 0,
    pagina:       query.data?.pagina ?? params.pagina,
    tamanhoPagina: query.data?.tamanhoPagina ?? params.tamanhoPagina,
    isLoading:    query.isLoading,
    isFetching:   query.isFetching,
    error:        query.error,
    createPerson: createMutation.mutate,
    updatePerson: updateMutation.mutate,
    deletePerson: deleteMutation.mutate,
    isCreating:   createMutation.isPending,
    isUpdating:   updateMutation.isPending,
    isDeleting:   deleteMutation.isPending,
  };
};

export const usePersonSearch = (termo: string, enabled: boolean) => {
  return useQuery({
    queryKey: ["persons-search", termo],
    queryFn: () => personService.search(termo),
    enabled: enabled && termo.trim().length >= 2,
  });
};

export const usePerson = (id: string | undefined) => {
  return useQuery({
    queryKey: ["persons", id],
    queryFn: () => personService.getById(id!),
    enabled: !!id,
  });
};
